import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { X, Brain, Loader2, CheckCircle, ChevronRight, ChevronDown, ChevronUp, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useKnowledgeAssessment, type GeneratingProgress } from "../../hooks/useKnowledgeAssessment";
import { KnowledgeQuiz } from "./KnowledgeQuiz";
import type { RoadmapArea } from "../../data/ch_concepts";

interface AssessmentQuizModalProps {
  userId: number;
  onClose: () => void;
  /** Вызывается после успешного завершения квиза (assessment сохранён) */
  onComplete?: () => void;
}

// ─── Generating screen with cycling topic labels ─────────────────────────────

function ModalGeneratingScreen({ progress }: { progress: GeneratingProgress | null }) {
  const tokenStream = progress?.tokenStream ?? "";

  const [displayText, setDisplayText] = useState("Анализирую ответы...");
  const [visible, setVisible] = useState(true);
  const prevCountRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const streamedLabels = useMemo(() => {
    if (!tokenStream) return [];
    const regex = /"label":\s*"([^"\\]+)"/g;
    const labels: string[] = [];
    let match;
    while ((match = regex.exec(tokenStream)) !== null) labels.push(match[1]);
    return [...new Set(labels)];
  }, [tokenStream]);

  const fadeTo = useCallback((text: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(false);
    timerRef.current = setTimeout(() => {
      setDisplayText(text);
      setVisible(true);
    }, 250);
  }, []);

  useEffect(() => {
    const count = streamedLabels.length;
    if (count === 0 || count <= prevCountRef.current) return;
    prevCountRef.current = count;
    if (count === 6) { fadeTo("Ещё чуть-чуть..."); return; }
    if (count === 14) { fadeTo("Ещё совсем чуть-чуть..."); return; }
    fadeTo(streamedLabels[count - 1]);
  }, [streamedLabels, fadeTo]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4 px-6 text-center">
      <Loader2 className="w-8 h-8 text-primary animate-spin" />
      <p
        className="text-sm text-neutral-text/70 font-medium min-h-[1.5em]"
        style={{ opacity: visible ? 1 : 0, transition: "opacity 0.25s ease" }}
      >
        {displayText}
      </p>
      <p className="text-xs text-neutral-text/40">
        {progress?.status ?? "Подготовка..."}
      </p>
    </div>
  );
}

// ─── Done screen: accordion preview + fixed buttons ───────────────────────────

const TOPIC_STATUS_DOT: Record<string, string> = {
  known: "bg-green-500 border-green-500",
  next: "bg-blue-500 border-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.2)]",
  to_learn: "bg-white border-gray-300",
};

function PreviewAreaRow({
  area,
  isOpen,
  onToggle,
  onTopicClick,
}: {
  area: RoadmapArea;
  isOpen: boolean;
  onToggle: () => void;
  onTopicClick?: (topicId: string, areaLabel: string, areaDescription: string) => void;
}) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <div className="border border-neutral-border/40 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start gap-2.5 px-3 py-2.5 bg-white hover:bg-gray-50/80 transition-colors text-left"
      >
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold text-neutral-text leading-snug">{area.label}</span>
          <p className="text-[11px] text-neutral-text/50 mt-0.5 leading-snug">{area.description}</p>
        </div>
        <div className="flex-shrink-0 flex items-center mt-0.5">
          {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-neutral-text/40" /> : <ChevronDown className="w-3.5 h-3.5 text-neutral-text/40" />}
        </div>
      </button>
      {isOpen && (
        <div className="border-t border-neutral-border/40 bg-gray-50/40 px-3 py-2 space-y-1">
          {area.topics.map((topic) => {
            const isKnown = topic.status === "known";
            const isNext = topic.status === "next";
            const isHovered = hoveredId === topic.id;
            return (
              <div key={topic.id} className="relative">
                <button
                  type="button"
                  onClick={() => onTopicClick?.(topic.id, area.label, area.description)}
                  onMouseEnter={() => setHoveredId(topic.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg border border-transparent hover:border-primary/20 hover:bg-primary/[0.03] transition-colors text-left"
                >
                  <div className={`w-2.5 h-2.5 rounded-full border-2 flex-shrink-0 ${TOPIC_STATUS_DOT[topic.status] ?? TOPIC_STATUS_DOT.to_learn}`} />
                  <span
                    className={`text-xs truncate flex-1 min-w-0 ${
                      isKnown ? "text-green-700" : isNext ? "text-blue-600" : "text-neutral-500"
                    }`}
                  >
                    {topic.label}
                  </span>
                  {isNext && (
                    <span className="text-[10px] font-semibold text-blue-500 bg-blue-50 rounded-full px-1.5 py-0.5 flex-shrink-0 border border-blue-200">
                      Изучить
                    </span>
                  )}
                  {isKnown && <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />}
                </button>
                {/* Tooltip */}
                {isHovered && (
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-10 bg-neutral-900 text-white rounded-lg px-3 py-1.5 text-[11px] font-medium shadow-lg whitespace-nowrap pointer-events-none">
                    Нажми, чтобы открыть урок
                    <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-x-[5px] border-x-transparent border-t-[5px] border-t-neutral-900" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ModalDoneScreen({
  areas,
  summary,
  onClose,
  onComplete,
}: {
  areas: RoadmapArea[];
  summary: string | null;
  onClose: () => void;
  onComplete?: () => void;
}) {
  const navigate = useNavigate();

  const handleTopicClick = useCallback(
    (topicId: string, areaLabel: string, areaDescription: string) => {
      const area = areas.find((a) => a.topics.some((t) => t.id === topicId));
      const topic = area?.topics.find((t) => t.id === topicId);
      if (!topic) return;
      onClose();
      onComplete?.();
      navigate("/ai-tutor", {
        state: {
          openTopic: {
            node: topic,
            areaLabel,
            areaDescription,
          },
        },
      });
    },
    [areas, navigate, onClose, onComplete],
  );
  const [openAreaIds, setOpenAreaIds] = useState<Set<string>>(() => {
    const first = areas.find((a) => a.topics.some((t) => t.status === "next" || t.status === "known"));
    return new Set(first ? [first.id] : areas[0] ? [areas[0].id] : []);
  });

  const handleDismiss = () => {
    onClose();
    onComplete?.();
  };

  const toggleArea = (id: string) => {
    setOpenAreaIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Scrollable preview */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-neutral-text">Roadmap построен!</h3>
              <p className="text-xs text-neutral-text/60 leading-relaxed">
                Результаты оценки сохранены. Теперь AI Tutor будет учитывать ваш текущий уровень и карту знаний в ответах — глубину объяснений и примеры можно подстроить под вас.
              </p>
            </div>
          </div>

          {summary && summary.trim() && (
            <div className="rounded-xl border border-neutral-border/60 bg-neutral-light/30 px-3 py-2">
              <p className="text-[10px] font-medium text-neutral-text/60 uppercase tracking-wide mb-1">Заключение об опыте</p>
              <p className="text-xs text-neutral-text whitespace-pre-wrap leading-snug">{summary.trim()}</p>
            </div>
          )}

          {areas.length > 0 && (
            <div className="space-y-2">
              {areas.map((area) => (
                <PreviewAreaRow
                  key={area.id}
                  area={area}
                  isOpen={openAreaIds.has(area.id)}
                  onToggle={() => toggleArea(area.id)}
                  onTopicClick={handleTopicClick}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Fixed footer */}
      <div className="flex-shrink-0 flex flex-col gap-2 px-5 py-4 border-t border-neutral-border bg-white">
        <button
          type="button"
          onClick={() => {
            handleDismiss();
            navigate("/courses", { state: { showRoadmap: true } });
          }}
          className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          Посмотреть полный роадмап
          <ChevronRight className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="w-full py-2.5 rounded-xl border border-neutral-border text-sm font-medium text-neutral-text hover:bg-neutral-light transition-colors"
        >
          Закрыть
        </button>
      </div>
    </div>
  );
}

// ─── Main modal ──────────────────────────────────────────────────────────────

/**
 * Модальное окно для прохождения полного unified-квиза.
 * Используется из профиля и настроек AI Tutor.
 */
export function AssessmentQuizModal({ userId, onClose, onComplete }: AssessmentQuizModalProps) {
  const {
    state,
    assessment,
    progress,
    error,
    submitQuiz,
    resetAssessment,
    startQuiz,
    fetchAdaptiveConcepts,
  } = useKnowledgeAssessment(null); // null чтобы не загружать existing assessment

  // Блокируем скролл страницы под модалкой — скроллится только контент модалки
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleSubmit = async (answers: Record<string, unknown>) => {
    await submitQuiz(answers, userId);
  };

  const isGenerating = state === "generating";
  const isDone = state === "roadmap";
  const isError = state === "error";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 overflow-hidden"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Оценка знаний ClickHouse"
    >
      <div
        className="w-full max-w-lg h-[90vh] max-h-[90vh] flex flex-col bg-white rounded-2xl shadow-xl border border-neutral-border overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary" />
            <h2 className="text-sm font-semibold text-neutral-text">Оценка знаний ClickHouse</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isGenerating}
            className="p-1.5 rounded-lg hover:bg-neutral-light text-neutral-text/70 hover:text-neutral-text disabled:opacity-50"
            aria-label="Закрыть"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {(state === "idle" || state === "quiz") && (
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <KnowledgeQuiz
                onSubmit={handleSubmit}
                isSubmitting={false}
                onFetchConcepts={fetchAdaptiveConcepts}
              />
            </div>
          )}

          {isGenerating && <ModalGeneratingScreen progress={progress} />}

          {isDone && (
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <ModalDoneScreen
                areas={assessment?.areas ?? progress?.areas ?? []}
                summary={assessment?.summary ?? progress?.summary ?? null}
                onClose={onClose}
                onComplete={onComplete}
              />
            </div>
          )}

          {isError && (
            <div className="flex flex-col items-center justify-center py-16 gap-4 px-6 text-center">
              <p className="text-sm text-red-600">{error ?? "Произошла ошибка"}</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={() => { resetAssessment(); startQuiz(); }}
                  className="px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  Попробовать снова
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 rounded-xl border border-neutral-border text-sm font-medium text-neutral-text hover:bg-neutral-light transition-colors"
                >
                  Закрыть
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
